import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import './StocktakeConduct.css';

const StocktakeConduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stocktake, setStocktake] = useState(null);
  const [stocktakeItems, setStocktakeItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [countInput, setCountInput] = useState('');

  useEffect(() => {
    fetchStocktakeData();
  }, [id]);

  useEffect(() => {
    filterItems();
  }, [stocktakeItems, searchTerm, filterStatus]);

  const fetchStocktakeData = async () => {
    try {
      setLoading(true);
      
      const [stocktakeResponse, itemsResponse] = await Promise.all([
        apiClient.get(`/stocktake/${id}`),
        apiClient.get(`/stocktake/${id}/items`)
      ]);
      
      setStocktake(stocktakeResponse.data);
      setStocktakeItems(itemsResponse.data);
    } catch (error) {
      console.error('Error fetching stocktake data:', error);
      setError('Failed to load stocktake data');
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = stocktakeItems;
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.item?.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.item?.itemCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }
    
    setFilteredItems(filtered);
  };

  const handleCountSubmit = async (stocktakeItemId, countedQuantity) => {
    try {
      await apiClient.put(`/stocktake/items/${stocktakeItemId}/count`, 
        { countedQuantity: parseInt(countedQuantity) }
      );
      
      // Update local state
      setStocktakeItems(prev => prev.map(item => 
        item.stocktakeItemId === stocktakeItemId 
          ? { ...item, countedQuantity: parseInt(countedQuantity), status: 'Counted' }
          : item
      ));
      
      setCountInput('');
      
      // Move to next item if in sequential mode
      if (currentItemIndex < filteredItems.length - 1) {
        setCurrentItemIndex(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error updating count:', error);
      setError('Failed to update count');
    }
  };

  const handleCompleteStocktake = async () => {
    if (window.confirm('Are you sure you want to complete this stocktake? This action cannot be undone.')) {
      try {
        await apiClient.post(`/stocktake/${id}/complete`, {});
        
        navigate(`/inventory/stocktakes/${id}`);
      } catch (error) {
        console.error('Error completing stocktake:', error);
        setError('Failed to complete stocktake');
      }
    }
  };

  const getProgressStats = () => {
    const total = stocktakeItems.length;
    const counted = stocktakeItems.filter(item => item.status === 'Counted').length;
    const pending = total - counted;
    
    return { total, counted, pending, percentage: total > 0 ? Math.round((counted / total) * 100) : 0 };
  };

  const currentItem = filteredItems[currentItemIndex];
  const stats = getProgressStats();

  if (loading) return <div className="loading">Loading stocktake...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!stocktake) return <div className="error">Stocktake not found</div>;

  return (
    <div className="stocktake-conduct">
      <div className="conduct-header">
        <div className="header-info">
          <h1>{stocktake.stocktakeName}</h1>
          <p className="stocktake-description">{stocktake.description}</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => navigate(`/inventory/stocktakes/${id}`)}
            className="btn btn-secondary"
          >
            View Details
          </button>
          <button 
            onClick={handleCompleteStocktake}
            className="btn btn-success"
            disabled={stats.counted === 0}
          >
            Complete Stocktake
          </button>
        </div>
      </div>

      <div className="progress-section">
        <div className="progress-stats">
          <div className="stat-item">
            <span className="stat-number">{stats.counted}</span>
            <span className="stat-label">Counted</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${stats.percentage}%` }}
          ></div>
        </div>
        <div className="progress-text">{stats.percentage}% Complete</div>
      </div>

      <div className="filters-section">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="status-filter">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Items</option>
            <option value="Pending">Pending</option>
            <option value="Counted">Counted</option>
          </select>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="no-items">
          <p>No items found matching your criteria.</p>
        </div>
      ) : (
        <div className="counting-section">
          {currentItem && (
            <div className="current-item-card">
              <div className="item-header">
                <h3>{currentItem.item?.itemName}</h3>
                <span className="item-code">{currentItem.item?.itemCode}</span>
              </div>
              <div className="item-details">
                <div className="detail-row">
                  <span className="label">System Quantity:</span>
                  <span className="value">{currentItem.systemQuantity}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Current Status:</span>
                  <span className={`status ${currentItem.status.toLowerCase()}`}>
                    {currentItem.status}
                  </span>
                </div>
                {currentItem.countedQuantity !== null && (
                  <div className="detail-row">
                    <span className="label">Counted Quantity:</span>
                    <span className="value">{currentItem.countedQuantity}</span>
                  </div>
                )}
              </div>
              
              <div className="count-input-section">
                <label htmlFor="countInput">Enter Counted Quantity:</label>
                <div className="count-input-group">
                  <input
                    id="countInput"
                    type="number"
                    min="0"
                    value={countInput}
                    onChange={(e) => setCountInput(e.target.value)}
                    className="count-input"
                    placeholder="0"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && countInput !== '') {
                        handleCountSubmit(currentItem.stocktakeItemId, countInput);
                      }
                    }}
                  />
                  <button
                    onClick={() => handleCountSubmit(currentItem.stocktakeItemId, countInput)}
                    disabled={countInput === ''}
                    className="btn btn-primary"
                  >
                    Update Count
                  </button>
                </div>
              </div>
              
              <div className="navigation-buttons">
                <button
                  onClick={() => setCurrentItemIndex(Math.max(0, currentItemIndex - 1))}
                  disabled={currentItemIndex === 0}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span className="item-counter">
                  {currentItemIndex + 1} of {filteredItems.length}
                </span>
                <button
                  onClick={() => setCurrentItemIndex(Math.min(filteredItems.length - 1, currentItemIndex + 1))}
                  disabled={currentItemIndex === filteredItems.length - 1}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          <div className="items-grid">
            {filteredItems.map((item, index) => (
              <div 
                key={item.stocktakeItemId} 
                className={`item-card ${index === currentItemIndex ? 'active' : ''} ${item.status.toLowerCase()}`}
                onClick={() => setCurrentItemIndex(index)}
              >
                <div className="item-name">{item.item?.itemName}</div>
                <div className="item-quantities">
                  <span>System: {item.systemQuantity}</span>
                  {item.countedQuantity !== null && (
                    <span>Counted: {item.countedQuantity}</span>
                  )}
                </div>
                <div className={`item-status ${item.status.toLowerCase()}`}>
                  {item.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StocktakeConduct;